import { useState } from 'react';
import { ChevronDown, ThumbsUp } from 'lucide-react';

import {
  DEFAULT_REACTION_OPTIONS,
  buildReactionMenuLabel,
  orderReactionEntries,
  type ReactionSummaryMap,
} from '../../shared/community';

type ReactionBarProps = {
  reactions: ReactionSummaryMap;
  onToggleReaction: (emoji: string) => void | Promise<void>;
  disabled?: boolean;
  options?: readonly string[];
  className?: string;
  variant?: 'chips' | 'summary' | 'action';
  onPrimaryAction?: () => void | Promise<void>;
  showAddButton?: boolean;
};

const cx = (...values: Array<string | false | null | undefined>) => values.filter(Boolean).join(' ');

export default function ReactionBar({
  reactions,
  onToggleReaction,
  disabled = false,
  options = DEFAULT_REACTION_OPTIONS,
  className,
  variant = 'chips',
  onPrimaryAction,
  showAddButton = true,
}: ReactionBarProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const entries = orderReactionEntries(reactions);
  const mineEntry = entries.find(([, value]) => value.mine);

  if (variant === 'action') {
    const activeEmoji = mineEntry?.[0];
    const activeLabel = activeEmoji
      ? buildReactionMenuLabel(activeEmoji).replace(`${activeEmoji} `, '')
      : 'Like';

    return (
      <div
        className={cx('group relative flex min-w-0 items-stretch justify-center', className)}
        onMouseEnter={() => setPickerOpen(true)}
        onMouseLeave={() => setPickerOpen(false)}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (onPrimaryAction) {
              void onPrimaryAction();
              return;
            }
            void onToggleReaction(activeEmoji ?? '❤️');
          }}
          className={cx(
            'inline-flex min-h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-l-md px-3 text-sm font-semibold transition-colors hover:bg-muted/70',
            activeEmoji ? 'text-primary' : 'text-muted-foreground',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          {activeEmoji ? <span aria-hidden="true">{activeEmoji}</span> : <ThumbsUp size={17} />}
          <span className="truncate">{activeLabel}</span>
        </button>
        <button
          type="button"
          aria-label="Choose a reaction"
          aria-expanded={pickerOpen}
          disabled={disabled}
          onClick={() => setPickerOpen((current) => !current)}
          className="inline-flex min-h-10 items-center justify-center rounded-r-md px-1.5 text-muted-foreground transition-colors hover:bg-muted/70 hover:text-primary disabled:opacity-50"
        >
          <ChevronDown size={14} />
        </button>

        {pickerOpen && !disabled ? (
          <div className="absolute bottom-[calc(100%+6px)] left-1/2 z-30 flex -translate-x-1/2 gap-1 rounded-full border border-border bg-background p-1.5 shadow-lg">
            {options.map((emoji) => (
              <button
                key={emoji}
                type="button"
                title={buildReactionMenuLabel(emoji)}
                aria-label={buildReactionMenuLabel(emoji)}
                onClick={() => {
                  setPickerOpen(false);
                  void onToggleReaction(emoji);
                }}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-lg transition-transform hover:scale-110 hover:bg-primary/10"
              >
                {emoji}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  if (!entries.length && (disabled || !showAddButton)) {
    return null;
  }

  return (
    <div className={cx(variant === 'chips' && "mt-2", "flex flex-wrap items-center gap-2", className)}>
      {entries.map(([emoji, value]) => (
        <button
          key={emoji}
          type="button"
          disabled={disabled}
          onClick={() => void onToggleReaction(emoji)}
          className={cx(
            "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold transition-colors",
            value.mine
              ? "border-primary/20 bg-primary/10 text-primary"
              : "border-border bg-background text-muted-foreground",
            disabled && "cursor-default opacity-90",
          )}
        >
          {emoji} {value.count}
        </button>
      ))}

      {!disabled && showAddButton && variant !== 'summary' ? (
        <>
          <button
            type="button"
            onClick={() => setPickerOpen((current) => !current)}
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/15"
          >
            <span aria-hidden="true">🙂</span>
            <span>React</span>
          </button>

          {pickerOpen ? (
            <div className="flex flex-wrap gap-2 rounded-full bg-muted/70 px-2 py-1">
              {options.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  title={buildReactionMenuLabel(emoji)}
                  onClick={() => {
                    setPickerOpen(false);
                    void onToggleReaction(emoji);
                  }}
                  className="inline-flex min-w-10 items-center justify-center rounded-full bg-background px-3 py-1.5 text-base hover:bg-primary/10"
                >
                  {emoji}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
